import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getLocalizedApiMessage } from "../../../utils/apiMessageHelper";
import {
  createCompanyRequest,
  deleteCompanyRequest,
  getCompanies,
  getCompanySizes,
  getCountries,
  getIndustries,
  updateCompanyRequest,
} from "../../../services/api/companies/companiesApi";

const rejectApiResponse = (responseData, fallbackMessage) => {
  if (responseData?.success === false) {
    return responseData.message ?? fallbackMessage;
  }
  return null;
};

export const fetchIndustries = createAsyncThunk(
  "companies/fetchIndustries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getIndustries();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch industries");
    }
  },
);

export const fetchCompanySizes = createAsyncThunk(
  "companies/fetchCompanySizes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCompanySizes();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch company sizes");
    }
  },
);

export const fetchCountries = createAsyncThunk(
  "companies/fetchCountries",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCountries();
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch countries");
    }
  },
);

export const fetchCompanies = createAsyncThunk(
  "companies/fetchCompanies",
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const response = await getCompanies(dateRange);
      return response.data.data;
    } catch (error) {
      return rejectWithValue("Failed to fetch companies");
    }
  },
);

export const createCompany = createAsyncThunk(
  "companies/createCompany",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await createCompanyRequest(formData);
      const apiError = rejectApiResponse(
        response.data,
        "Failed to create company",
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        company: response.data.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, "Failed to create company"),
      );
    }
  },
);

export const updateCompany = createAsyncThunk(
  "companies/updateCompany",
  async ({ companyId, formData }, { rejectWithValue }) => {
    try {
      const response = await updateCompanyRequest(companyId, formData);
      const apiError = rejectApiResponse(
        response.data,
        "Failed to update company",
      );
      if (apiError) {
        return rejectWithValue(apiError);
      }

      return {
        company: response.data.data,
        message: response.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        getLocalizedApiMessage(error, "Failed to update company"),
      );
    }
  },
);

export const deleteCompany = createAsyncThunk(
  "companies/deleteCompany",
  async (companyId, { rejectWithValue }) => {
    try {
      await deleteCompanyRequest(companyId);
      return companyId;
    } catch (error) {
      return rejectWithValue("Failed to delete company");
    }
  },
);

const companiesSlice = createSlice({
  name: "companies",
  initialState: {
    companyData: [],
    companyCount: 0,
    industries: [],
    companySizes: [],
    countries: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companyData = action.payload.companies;
        state.companyCount = action.payload.count;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchIndustries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchIndustries.fulfilled, (state, action) => {
        state.loading = false;
        state.industries = action.payload;
      })
      .addCase(fetchIndustries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompanySizes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanySizes.fulfilled, (state, action) => {
        state.loading = false;
        state.companySizes = action.payload;
      })
      .addCase(fetchCompanySizes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.countries = action.payload;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        if (action.payload?.company) {
          state.companyData.unshift(action.payload.company);
        }
      })
      .addCase(createCompany.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        const company = action.payload?.company;
        if (!company) return;

        const index = state.companyData.findIndex(
          (item) => item.id === company.id,
        );
        if (index !== -1) {
          state.companyData[index] = company;
        }
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteCompany.fulfilled, (state, action) => {
        state.companyData = state.companyData.filter(
          (company) => company.id !== action.payload,
        );
      })
      .addCase(deleteCompany.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export default companiesSlice.reducer;
